import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { gameWsUrl } from "@/lib/ws";
import { ensureAudio, playTick } from "@/lib/sfx";
import { quizImageUrl } from "@/lib/quizImage";
import Logo from "@/components/Logo";
import { GamePinBar } from "@/components/GamePinBar";
import { Triangle, Diamond, Circle, Square, Loader2, Trophy, Check, X, Clock, Flame } from "lucide-react";

const shapes = [Triangle, Diamond, Circle, Square];
const colors = ["bg-brand-blue", "bg-brand-purple", "bg-brand-green", "bg-brand-yellow"];

export default function Sala() {
  const { pin } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const wsRef = useRef(null);
  const [info] = useState(() =>
    location.state || JSON.parse(sessionStorage.getItem(`educarte_join_${pin}`) || "null")
  );
  const [phase, setPhase] = useState("connecting");
  const [players, setPlayers] = useState(0);
  const [question, setQuestion] = useState(null);
  const [selected, setSelected] = useState([]);
  const [timeLeft, setTimeLeft] = useState(0);
  const [reveal, setReveal] = useState(null);
  const [ranking, setRanking] = useState(null);
  const [ended, setEnded] = useState(null);
  const [revealCountdown, setRevealCountdown] = useState(null);
  const [score, setScore] = useState(0);
  const [online, setOnline] = useState(false);

  const send = useCallback((msg) => {
    if (wsRef.current?.readyState === 1) wsRef.current.send(JSON.stringify(msg));
  }, []);

  useEffect(() => {
    if (!info) {
      navigate("/jogar");
      return;
    }
    sessionStorage.setItem(`educarte_join_${pin}`, JSON.stringify(info));
    let closing = false;
    let reconnectTimer = null;
    let lastMsg = Date.now();
    let qIndex = null;
    const sync = { status: "lobby", index: -1 };

    const handleMessage = (e) => {
      lastMsg = Date.now();
      const m = JSON.parse(e.data);
      if (m.type === "sync_ack") return;
      if (m.type === "lobby") setPlayers(m.count);
      else if (m.type === "question") {
        sync.status = "question";
        sync.index = m.index;
        if (qIndex === m.index) return; // duplicate re-sync of same question — keep current selection/phase
        qIndex = m.index;
        setQuestion(m);
        setSelected([]);
        setReveal(null);
        setRanking(null);
        setPhase("question");
      } else if (m.type === "answer_ack") {
        sync.status = "question";
        setPhase((p) => (p === "question" || p === "answered" ? "answered" : p));
      } else if (m.type === "reveal_countdown") {
        setRevealCountdown(m.seconds || 2);
        setPhase("revealing");
      } else if (m.type === "reveal") {
        sync.status = "reveal";
        sync.index = m.index;
        setReveal(m);
        setScore(m.total_score ?? 0);
        setPhase("reveal");
      } else if (m.type === "ranking") {
        sync.status = "ranking";
        sync.index = m.index;
        setRanking(m);
        setPhase("ranking");
      } else if (m.type === "ended") {
        sync.status = "ended";
        sync.index = -1;
        setEnded(m);
        setPhase("ended");
      } else if (m.type === "kicked") {
        closing = true;
        alert("Você foi removido da sala pelo professor.");
        navigate("/jogar");
      } else if (m.type === "state" && m.status === "lobby") {
        sync.status = "lobby";
        sync.index = -1;
        setPhase((p) => (p === "connecting" ? "waiting" : p));
      }
    };

    const connect = () => {
      const ws = new WebSocket(gameWsUrl(info.session_id, { role: "player", pid: info.participant_id }));
      wsRef.current = ws;
      ws.onopen = () => {
        lastMsg = Date.now();
        setOnline(true);
        setPhase((p) => (p === "connecting" ? "waiting" : p));
      };
      ws.onmessage = handleMessage;
      ws.onclose = () => {
        setOnline(false);
        if (closing) return;
        reconnectTimer = setTimeout(connect, 1500);
      };
      ws.onerror = () => {
        try { ws.close(); } catch (_) {}
      };
    };
    connect();

    // Periodic sync: self-heal any missed update and detect dead (half-open) sockets.
    const syncTimer = setInterval(() => {
      const ws = wsRef.current;
      if (ws?.readyState === 1) {
        ws.send(JSON.stringify({ action: "sync", status: sync.status, index: sync.index }));
        if (Date.now() - lastMsg > 10000) {
          try { ws.close(); } catch (_) {}
        }
      }
    }, 3000);

    return () => {
      closing = true;
      clearInterval(syncTimer);
      if (reconnectTimer) clearTimeout(reconnectTimer);
      wsRef.current?.close();
    };
    // eslint-disable-next-line
  }, []);

  // countdown timer
  useEffect(() => {
    if (phase !== "question" || !question?.ends_at) return;
    const tick = () => setTimeLeft(Math.max(0, Math.round(question.ends_at - Date.now() / 1000)));
    tick();
    const id = setInterval(tick, 250);
    return () => clearInterval(id);
  }, [phase, question]);

  // reveal countdown (all answered before the timer, auto mode): 2, 1
  useEffect(() => {
    if (phase !== "revealing") return;
    ensureAudio();
    playTick();
    const id = setInterval(() => {
      setRevealCountdown((n) => {
        const next = (n ?? 1) - 1;
        if (next >= 1) playTick();
        return next > 0 ? next : (n ?? 1);
      });
    }, 1000);
    return () => clearInterval(id);
  }, [phase]);

  const answer = (oi) => {
    if (phase !== "question" || timeLeft <= 0) return;
    if (question.qtype === "multiple_answers") {
      setSelected((s) => (s.includes(oi) ? s.filter((x) => x !== oi) : [...s, oi]));
    } else {
      setSelected([oi]);
      send({ action: "answer", options: [oi] });
      setPhase("answered");
    }
  };

  const confirmMulti = () => {
    if (!selected.length) return;
    send({ action: "answer", options: selected });
    setPhase("answered");
  };

  const Header = ({ children }) => (
    <div className="flex items-center justify-between px-5 h-14 bg-white/10 backdrop-blur-xl">
      <Logo light />
      {children}
    </div>
  );

  if (!info) return null;

  const connBar = !online && phase !== "connecting" ? (
    <div className="fixed top-0 inset-x-0 z-50 flex justify-center pt-2 pointer-events-none">
      <div className="flex items-center gap-2 bg-amber-500 text-brand-ink rounded-full px-4 py-1.5 text-xs font-bold shadow-lg" data-testid="reconnecting-bar">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Reconectando…
      </div>
    </div>
  ) : null;

  // ENDED
  if (phase === "ended" && ended) {
    const pos = ended.leaderboard.findIndex((p) => p.pid === info.participant_id) + 1;
    return (
      <div className="min-h-screen bg-gradient-to-br from-brand-blue to-brand-purple text-white flex flex-col">
        <Header />
        {connBar}
        <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
          <Trophy className="w-16 h-16 text-brand-yellow" />
          <h1 className="mt-4 font-heading text-3xl font-black">Fim de jogo!</h1>
          <p className="mt-2 text-white/80">Você ficou em</p>
          <p className="font-heading text-6xl font-black">{pos || "-"}º</p>
          <p className="mt-1 text-white/80">{score} pontos</p>
          <div className="mt-8 w-full max-w-sm space-y-2">
            {ended.leaderboard.slice(0, 5).map((p, i) => (
              <div key={p.pid} className={`flex items-center justify-between rounded-xl px-4 py-3 ${p.pid === info.participant_id ? "bg-white text-brand-ink" : "bg-white/10"}`}>
                <span className="font-semibold">{p.avatar ? `${p.avatar} ` : ""}{i + 1}. {p.nickname}</span>
                <span className="font-heading font-bold">{p.score}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // REVELANDO — todos responderam antes do tempo (modo automático)
  if (phase === "revealing") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-brand-blue to-brand-purple text-white flex flex-col">
        <Header><span className="font-heading font-bold">{score} pts</span></Header>
        <GamePinBar pin={info.pin} />
        {connBar}
        <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
          <p className="uppercase tracking-widest text-brand-yellow font-bold mb-3">Todos responderam!</p>
          <p className="text-lg font-heading font-bold mb-2">Revelando a resposta em…</p>
          <div
            key={revealCountdown}
            className="font-heading font-black leading-none"
            style={{ fontSize: "7rem", animation: "countPop 1s ease-out both" }}
            data-testid="player-reveal-countdown"
          >
            {revealCountdown}
          </div>
        </div>
      </div>
    );
  }

  // REVEAL
  if (phase === "reveal" && reveal) {
    return (
      <div className={`min-h-screen text-white flex flex-col ${reveal.your_correct ? "bg-brand-green" : "bg-slate-800"}`}>
        <Header><span className="font-heading font-bold">{score} pts</span></Header>
        <GamePinBar pin={info.pin} />
        {connBar}
        <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
          <div className="w-20 h-20 rounded-full bg-white/20 flex items-center justify-center">
            {reveal.your_correct ? <Check className="w-10 h-10" /> : <X className="w-10 h-10" />}
          </div>
          <h1 className="mt-5 font-heading text-3xl font-black">{reveal.your_correct ? "Acertou! 🎉" : "Não foi dessa vez"}</h1>
          {reveal.your_correct && <p className="mt-2 text-xl">+{reveal.your_score} pontos</p>}
          {reveal.your_streak >= 2 && (
            <div className="mt-4 inline-flex items-center gap-2 bg-white/20 rounded-full px-5 py-2.5 font-heading font-black text-xl animate-pulse" data-testid="streak-badge">
              <Flame className="w-7 h-7 text-orange-300" /> {reveal.your_streak} em sequência!
            </div>
          )}
          {reveal.explanation && <p className="mt-4 max-w-md text-white/80 text-sm">{reveal.explanation}</p>}
          <div className="mt-8 w-full max-w-sm space-y-2">
            {reveal.leaderboard?.slice(0, 5).map((p, i) => (
              <div key={p.pid} className={`flex items-center justify-between rounded-xl px-4 py-2.5 ${p.pid === info.participant_id ? "bg-white text-brand-ink" : "bg-white/10"}`}>
                <span className="text-sm font-semibold">{p.avatar ? `${p.avatar} ` : ""}{i + 1}. {p.nickname}</span>
                <span className="font-bold text-sm">{p.score}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // RANKING (placar parcial) — acompanha o avanço do professor entre as perguntas
  if (phase === "ranking" && ranking) {
    const board = ranking.leaderboard || [];
    return (
      <div className="min-h-screen bg-gradient-to-br from-brand-blue to-brand-purple text-white flex flex-col">
        <Header><span className="font-heading font-bold">{score} pts</span></Header>
        <GamePinBar pin={info.pin} />
        {connBar}
        <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
          <Trophy className="w-12 h-12 text-brand-yellow" />
          <h1 className="mt-3 font-heading text-2xl font-black">Placar parcial</h1>
          <div className="mt-6 w-full max-w-sm space-y-2" data-testid="player-ranking-list">
            {board.length === 0 && <p className="text-white/60">Sem pontuações ainda.</p>}
            {board.map((p, i) => (
              <div key={p.pid} className={`flex items-center justify-between rounded-xl px-4 py-3 ${p.pid === info.participant_id ? "bg-white text-brand-ink" : "bg-white/10"}`}>
                <span className="font-semibold">{p.avatar ? `${p.avatar} ` : ""}{i + 1}. {p.nickname}</span>
                <span className="font-heading font-bold">{p.score}</span>
              </div>
            ))}
          </div>
          <p className="mt-8 text-sm text-white/70 flex items-center gap-2" data-testid="player-ranking-hint">
            <Loader2 className="w-4 h-4 animate-spin" /> {ranking.is_last ? "Fim do jogo em instantes…" : "Próxima pergunta em instantes…"}
          </p>
        </div>
      </div>
    );
  }

  // QUESTION
  if (phase === "question" && question) {
    return (
      <div className="min-h-screen bg-brand-ink text-white flex flex-col">
        <Header>
          <span className="flex items-center gap-1.5 bg-white/10 px-3 py-1 rounded-full text-sm"><Clock className="w-4 h-4" /> {timeLeft}s</span>
        </Header>
        <GamePinBar pin={info.pin} />
        {connBar}
        <div className="px-5 py-6 text-center">
          <p className="text-white/60 text-sm">Questão {question.index + 1} de {question.total}</p>
          <h1 className="mt-3 font-heading text-xl sm:text-2xl font-bold">{question.question}</h1>
          {question.image && (
            <div className="mt-3 flex justify-center">
              <img
                src={quizImageUrl(question.image)}
                alt="Figura da questão"
                className="max-h-48 rounded-xl object-contain border border-white/10 bg-black/20"
                data-testid="question-image"
              />
            </div>
          )}
        </div>
        <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-3 p-5 content-center">
          {question.options.map((opt, oi) => {
            const Shape = shapes[oi % 4];
            const sel = selected.includes(oi);
            return (
              <button
                key={oi}
                onClick={() => answer(oi)}
                disabled={timeLeft <= 0}
                data-testid={`answer-${oi}`}
                className={`flex items-center gap-3 p-5 rounded-2xl text-white font-semibold text-lg ${colors[oi % 4]} ${sel ? "ring-4 ring-white" : ""} active:scale-95 transition-transform disabled:opacity-50`}
              >
                <Shape className="w-6 h-6 shrink-0" fill="white" />
                <span className="text-left">{opt}</span>
              </button>
            );
          })}
        </div>
        {question.qtype === "multiple_answers" && (
          <div className="p-5">
            <button onClick={confirmMulti} className="w-full h-12 rounded-full bg-white text-brand-ink font-bold" data-testid="confirm-multi">Confirmar resposta</button>
          </div>
        )}
      </div>
    );
  }

  // ANSWERED
  if (phase === "answered") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-brand-blue to-brand-purple text-white flex flex-col">
        <Header><span className="font-heading font-bold">{score} pts</span></Header>
        <GamePinBar pin={info.pin} />
        {connBar}
        <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
          <Loader2 className="w-10 h-10 animate-spin" />
          <p className="mt-4 font-heading text-xl font-bold">Resposta enviada!</p>
          <p className="text-white/80">Aguarde os outros jogadores...</p>
        </div>
      </div>
    );
  }

  // WAITING / CONNECTING
  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-blue to-brand-purple text-white flex flex-col">
      <Header />
      {connBar}
      <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
        <div className="w-20 h-20 rounded-full bg-white/20 flex items-center justify-center font-heading text-4xl font-black" data-testid="player-avatar">
          {info.avatar || info.nickname?.[0]?.toUpperCase()}
        </div>
        <h1 className="mt-5 font-heading text-2xl font-black">Você está dentro, {info.nickname}!</h1>
        <p className="mt-2 text-white/80">Aguardando o professor iniciar...</p>
        <p className="mt-6 text-sm text-white/60">{players} jogador(es) na sala</p>
        <Loader2 className="w-6 h-6 animate-spin mt-4" />
      </div>
    </div>
  );
}
